import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, FileText, FileCheck2, Plus, Save, Flame, Package2, XCircle, Banknote, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CallHistoryCard } from "@/components/CallHistoryCard";
import { useDropdowns } from "@/hooks/useMeta";
import { DEAL_STAGES, formatINR, formatINRCr, formatDate, canDeleteLeadDeal } from "@/lib/constants";
import { StatusBadge, DivisionTag } from "@/components/StatusBadge";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";
import { VoiceNoteButton } from "@/components/VoiceNoteButton";
import { EventsCard } from "@/components/EventsCard";
import { RemindersCard } from "@/components/RemindersCard";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ReasonDialog } from "@/components/ReasonDialog";
import { WonDialog } from "@/components/WonDialog";
import { CompletenessBadge, CompletenessCard } from "@/components/CompletenessBadge";
import { DealInfoDialog } from "@/components/DealInfoDialog";
import { AssignOwnerSelect } from "@/components/AssignOwnerSelect";
import { UniverseLinkBanner } from "@/components/UniverseLinkBanner";
import { StageInfo } from "@/components/StageInfo";
import { NewDealDialog } from "@/pages/Deals";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const poUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t && path ? `${BACKEND}/api/opportunities/po/${path}?auth=${t}` : "";
};

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: dd } = useDropdowns();
  const [note, setNote] = useState("");
  const [val, setVal] = useState(null);
  const [req, setReq] = useState(null);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [wonOpen, setWonOpen] = useState(false);
  const [editWonOpen, setEditWonOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostComments, setLostComments] = useState("");
  const [billing, setBilling] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const setB = (k, v) => setBilling((b) => ({ ...b, [k]: v }));

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await api.get(`/opportunities/${id}`)).data,
  });

  const canEdit = true;

  const patch = async (body, msg) => {
    try {
      await api.put(`/opportunities/${id}`, body);
      qc.invalidateQueries({ queryKey: ["deal", id] });
      if (msg) toast.success(msg);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Deal deleted");
      navigate("/deals");
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const markLost = async () => {
    if (!lostReason) { toast.error("Select a reason"); return; }
    try {
      await api.post(`/opportunities/${id}/lost`, { lost_reason: lostReason, comments: lostComments });
      setLostOpen(false); setLostReason(""); setLostComments("");
      qc.invalidateQueries({ queryKey: ["deal", id] });
      toast.success("Deal marked as Lost");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/opportunities/${id}/notes`, { description: note });
    setNote(""); qc.invalidateQueries({ queryKey: ["deal", id] }); toast.success("Note added");
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!deal) return <p className="text-slate-400">Deal not found.</p>;

  const reqDetails = req ?? (deal.requirement_details?.text || "");
  const siteDetails = req !== null ? (deal.site_details?.text || "") : (deal.site_details?.text || "");

  return (
    <div>
      <button onClick={() => navigate("/deals")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Deals
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{deal.name}</h1>
            {deal.is_large_deal && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><Flame className="h-3 w-3" /> Large Deal</span>}
            {deal.status === "lost" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid="deal-lost-badge"><XCircle className="h-3 w-3" /> Lost</span>}
            {deal.stage === "Order Won" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Won</span>}
            {deal.stage === "Order Booked" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded-full" data-testid="deal-booked-badge">Booked</span>}
            {deal.stage === "Order Fulfilled" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full" data-testid="deal-fulfilled-badge">Fulfilled</span>}
            <CompletenessBadge completeness={deal.completeness} />
            <DivisionTag id={deal.division_id} />
          </div>
          <p className="text-slate-500 mt-1">{deal.company} · <span className="font-mono font-semibold text-slate-800">{formatINRCr(deal.deal_value)}</span> <span className="text-[11px] text-slate-400">INR Cr</span></p>
          {deal.status === "lost" && deal.lost_reason && (
            <p className="text-sm text-red-600 mt-1" data-testid="deal-lost-reason">Lost reason: {deal.lost_reason}{deal.lost_comments ? ` — ${deal.lost_comments}` : ""}
              {canEdit && <button onClick={() => { setLostReason(deal.lost_reason || ""); setLostComments(deal.lost_comments || ""); setLostOpen(true); }} className="ml-1.5 text-slate-400 hover:text-slate-700 align-middle" title="Edit lost reason" data-testid="edit-lost-reason-btn"><Pencil className="h-3 w-3 inline" /></button>}
            </p>
          )}
          {["Order Won", "Order Booked", "Order Fulfilled"].includes(deal.stage) && (
            <div className="mt-1">
              <p className="text-sm text-emerald-700" data-testid="deal-won-reason">Won reason: {deal.won_reason || "not recorded"}{deal.won_comments ? ` — ${deal.won_comments}` : ""}
                {canEdit && <button onClick={() => setEditWonOpen(true)} className="ml-1.5 text-slate-400 hover:text-slate-700 align-middle" title="Edit won reason" data-testid="edit-won-reason-btn"><Pencil className="h-3 w-3 inline" /></button>}
              </p>
              {deal.po_file && (
                <a href={poUrl(deal.po_file)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline underline-offset-2 mt-0.5" data-testid="deal-po-link">
                  <FileCheck2 className="h-3.5 w-3.5" /> View Purchase Order{deal.po_filename ? ` (${deal.po_filename})` : ""}
                </a>
              )}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <div>
              <div className="flex items-center gap-1"><Label className="text-xs text-slate-500">Pipeline Stage</Label><StageInfo kind="opp" /></div>
              <Select value={deal.stage} onValueChange={(v) => { if (v === "Order Won") setWonOpen(true); else if (v === "Order Lost") setLostOpen(true); else patch({ stage: v }, `Moved to ${v}`); }} disabled={deal.status === "lost"}>
                <SelectTrigger className="h-9 w-full sm:w-[240px] mt-1" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DEAL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1.5 flex-1" data-testid="deal-edit-btn"><Pencil className="h-4 w-4" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => setSampleOpen(true)} className="gap-1.5 flex-1" data-testid="deal-request-sample-btn"><Package2 className="h-4 w-4" /> Sample</Button>
              {deal.status !== "lost" && !["Order Won", "Order Booked", "Order Fulfilled"].includes(deal.stage) && (
                <Button variant="outline" size="sm" onClick={() => setLostOpen(true)} className="gap-1.5 flex-1 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50" data-testid="deal-mark-lost-btn"><XCircle className="h-4 w-4" /> Mark Lost</Button>
              )}
            </div>
            <AssignOwnerSelect entityType="opportunity" entityId={deal.id} ownerId={deal.owner_id}
              onAssigned={(res) => {
                const isManager = ["super_admin", "sales_head", "region_head", "sales_manager"].includes(user?.role);
                if (!isManager && res.owner_id !== user?.id) navigate("/deals");
                else qc.invalidateQueries({ queryKey: ["deal", id] });
              }} />
            <VoiceNoteButton entityType="opportunity" entityId={deal.id} company={deal.company} onSaved={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />
            {canDeleteLeadDeal(user) && (
              <DeleteButton variant="button" label="Delete Deal" testId="deal-delete-btn" title="Delete this deal?" description="This permanently removes the deal, its quotes, notes and activity. This cannot be undone." onConfirm={del} />
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {!deal.universe_id && !["factory", "management", "dealer", "marketing"].includes(user?.role) && (
            <UniverseLinkBanner entityType="deal" record={deal} onLinked={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />
          )}
          {deal.completeness && !deal.completeness.complete && canEdit && (
            <CompletenessCard completeness={deal.completeness} entityLabel="opportunity" onComplete={() => setInfoOpen(true)} />
          )}
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Deal Information</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Owner</p><p className="text-sm text-slate-800 mt-0.5">{deal.owner_name}</p></div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Rating</p>
                {canEdit ? (
                  <>
                    <Select value={deal.sub_rating || "None"} onValueChange={(v) => patch({ sub_rating: v === "None" ? "" : v }, "Rating updated")}>
                      <SelectTrigger className="h-8 w-[120px] mt-1" data-testid="deal-rating-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["None", "Hot", "Warm", "Cold"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400 mt-0.5">Overrides the auto Hot/Warm/Cold in BI</p>
                  </>
                ) : <p className="text-sm text-slate-800 mt-0.5">{deal.sub_rating || "—"}</p>}
              </div>
              <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Expected Close</p><p className="text-sm text-slate-800 mt-0.5">{formatDate(deal.expected_close_date)}</p></div>
              {deal.zone && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Zone</p><p className="text-sm text-slate-800 mt-0.5">{deal.zone}</p></div>}
              {deal.branch && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Branch</p><p className="text-sm text-slate-800 mt-0.5">{deal.branch}</p></div>}
              {deal.univic_specified && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">UniVic Specified</p><p className="text-sm text-slate-800 mt-0.5">{deal.univic_specified}</p></div>}
              {deal.dealer_involved && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Dealer Involved</p><p className="text-sm text-slate-800 mt-0.5">{deal.dealer_involved}</p></div>}
              {deal.universe_company && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Universe Company</p><p className="text-sm text-slate-800 mt-0.5">{deal.universe_company}</p></div>}
              {deal.identified && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Identified</p><p className="text-sm text-slate-800 mt-0.5">{deal.identified}</p></div>}
              {deal.architect && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Architect / Designer</p><p className="text-sm text-slate-800 mt-0.5">{deal.architect}</p></div>}
              {deal.dnb_gc && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">D&B / GC</p><p className="text-sm text-slate-800 mt-0.5">{deal.dnb_gc}</p></div>}
              {deal.end_client_name && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">End Client</p><p className="text-sm text-slate-800 mt-0.5">{deal.end_client_name}{deal.end_client_phone ? ` · ${deal.end_client_phone}` : ""}</p></div>}
              {deal.competition && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Competition</p><p className="text-sm text-slate-800 mt-0.5">{deal.competition}</p></div>}
              {deal.product_type && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Type of Product</p><p className="text-sm text-slate-800 mt-0.5">{deal.product_type}</p></div>}
              {deal.team_split?.length > 0 && (
                <div data-testid="deal-team-split">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Team Split</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {deal.team_split.map((m) => (
                      <span key={m.user_id} className="text-[11px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-medium">
                        {m.name}{m.region ? ` (${m.region})` : ""} — {m.pct}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {deal.area_lacs_sqft && <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Area (Lakh sqft)</p><p className="text-sm text-slate-800 mt-0.5">{deal.area_lacs_sqft}</p></div>}
            </div>
            {canEdit && (
              <button onClick={() => setInfoOpen(true)} className="mt-3 text-xs font-medium text-slate-500 hover:text-slate-800 underline underline-offset-2" data-testid="edit-deal-info-btn">
                Edit project / required info
              </button>
            )}
            {canEdit && (
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
                <div className="space-y-1.5"><Label className="text-xs">Deal Value (INR)</Label>
                  <div className="flex gap-2">
                    <Input type="number" defaultValue={deal.deal_value} onChange={(e) => setVal(e.target.value)} className="h-9" data-testid="deal-value-input" />
                    <Button size="sm" variant="outline" onClick={() => patch({ deal_value: Number(val ?? deal.deal_value) }, "Value updated")} data-testid="save-value-btn"><Save className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Expected Close Date</Label>
                  <Input type="date" defaultValue={deal.expected_close_date ? deal.expected_close_date.slice(0, 10) : ""} onChange={(e) => patch({ expected_close_date: e.target.value ? new Date(e.target.value).toISOString() : null })} className="h-9" data-testid="deal-close-input" />
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Requirement & Site Details</h3>
            <Textarea defaultValue={deal.requirement_details?.text || ""} onChange={(e) => setReq(e.target.value)} rows={4} placeholder="Capture requirements, specs, site conditions…" data-testid="requirement-input" disabled={!canEdit} />
            {canEdit && <Button size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800" onClick={() => patch({ requirement_details: { text: req ?? deal.requirement_details?.text } }, "Saved")} data-testid="save-requirement-btn">Save Requirements</Button>}
          </Card>

          {canEdit && ["Order Won", "Order Booked", "Order Fulfilled"].includes(deal.stage) && (
            <Card className="p-5 border-emerald-200" data-testid="billing-card">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1 flex items-center gap-1.5"><Banknote className="h-4 w-4 text-emerald-600" /> Billing & Collection</h3>
              <p className="text-xs text-slate-400 mb-4">Drives Pending Orders & Collection dashboards.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">PO Value (INR)</Label><Input type="number" defaultValue={deal.po_value ?? deal.deal_value} onChange={(e) => setB("po_value", e.target.value)} className="h-9" data-testid="po-value-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Expected Billing Month</Label><Input type="month" defaultValue={deal.expected_billing_month || ""} onChange={(e) => setB("expected_billing_month", e.target.value)} className="h-9" data-testid="billing-month-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Billed Amount (INR)</Label><Input type="number" defaultValue={deal.billed_amount ?? 0} onChange={(e) => setB("billed_amount", e.target.value)} className="h-9" data-testid="billed-amount-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Collected Amount (INR)</Label><Input type="number" defaultValue={deal.collected_amount ?? 0} onChange={(e) => setB("collected_amount", e.target.value)} className="h-9" data-testid="collected-amount-input" /></div>
              </div>
              <Button size="sm" className="mt-3 bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="save-billing-btn"
                onClick={() => patch({
                  po_value: Number(billing.po_value ?? deal.po_value ?? deal.deal_value ?? 0),
                  billed_amount: Number(billing.billed_amount ?? deal.billed_amount ?? 0),
                  collected_amount: Number(billing.collected_amount ?? deal.collected_amount ?? 0),
                  expected_billing_month: billing.expected_billing_month ?? deal.expected_billing_month ?? "",
                }, "Billing saved")}><Save className="h-3.5 w-3.5" /> Save Billing</Button>
            </Card>
          )}

          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-sm text-slate-800">Quotations</h3>
              {canEdit && <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => navigate(`/quotes/new?opp=${id}`)} data-testid="create-quote-btn"><Plus className="h-4 w-4" /> Create Quotation</Button>}
            </div>
            {(deal.quotations || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No quotations yet.</p>
            ) : (
              <div className="space-y-2">
                {deal.quotations.map((q) => (
                  <button key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="w-full flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left" data-testid={`quote-link-${q.id}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium">{q.quote_number}</span>
                      <span className="text-xs text-slate-400">v{q.version_number}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{formatINR(q.total_amount)}</span>
                      <StatusBadge kind="quote" value={q.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <EventsCard entityType="opportunity" entityId={deal.id} company={deal.company} canEdit={canEdit} onSaved={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />

          <CallHistoryCard linkedType="opportunity" linkedId={deal.id} company={deal.company} />
        </div>

        <div className="space-y-4">
          {canEdit && <RemindersCard entityType="opportunity" entityId={deal.id} />}
          {canEdit && (
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Add Note</h3>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log activity…" rows={3} data-testid="deal-note-input" />
              <Button onClick={addNote} size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800 w-full" data-testid="deal-add-note-btn">Add to timeline</Button>
            </Card>
          )}
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Activity Timeline</h3>
            <ActivityTimeline activities={deal.activities} />
          </Card>
        </div>
      </div>

      <SampleRequestDialog open={sampleOpen} onOpenChange={setSampleOpen}
        context={{ opportunity_id: deal.id, company: deal.company }}
        onSaved={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />

      <DealInfoDialog open={infoOpen} onOpenChange={setInfoOpen} deal={deal}
        onSaved={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />
      <NewDealDialog open={editOpen} onOpenChange={setEditOpen} editDeal={deal}
        onSaved={() => qc.invalidateQueries({ queryKey: ["deal", id] })} />

      <WonDialog open={wonOpen} onOpenChange={setWonOpen}
        onConfirm={async (p) => { await patch({ stage: "Order Won", ...p }, "Marked as Won — PO on file"); setWonOpen(false); }} />

      <ReasonDialog open={editWonOpen} onOpenChange={setEditWonOpen} kind="won"
        title="Edit won reason" confirmLabel="Save"
        initialReason={deal.won_reason || ""} initialComments={deal.won_comments || ""}
        onConfirm={async (r, c) => { await patch({ won_reason: r, won_comments: c }, "Won reason updated"); setEditWonOpen(false); }} />

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="max-w-md" data-testid="mark-lost-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><XCircle className="h-4 w-4 text-red-600" /> Mark Deal as Lost</DialogTitle>
            <DialogDescription>Record why this deal was lost for win/loss reporting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Lost Reason <span className="text-red-500">*</span></Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger className="h-9" data-testid="lost-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>{(dd?.lost_reason || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Comments</Label>
              <Textarea value={lostComments} onChange={(e) => setLostComments(e.target.value)} rows={2} placeholder="Optional context (e.g. competitor name, price)" data-testid="lost-comments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostOpen(false)} data-testid="lost-cancel">Cancel</Button>
            <Button onClick={markLost} className="bg-red-600 hover:bg-red-700 text-white" data-testid="lost-confirm">Mark as Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
